import * as t from 'io-ts';
import uuid from 'uuid-random';

import {makeApiType, makeEnumRuntimeType} from './apiTypes';

export enum DataFieldsTableColumnTypesEnum {
  BOOLEAN = 'boolean',
  CONTACT = 'contact',
  INBOX = 'inbox',
  NUMBER = 'number',
  STRING = 'string',
  TAG = 'tag',
  TEAMMATE = 'teammate',
}

interface DataFieldsTableColumnIdBrand {
  readonly ColumnId: unique symbol;
}

export const RDataFieldsTableColumnId = t.brand(
  t.string,
  (str): str is t.Branded<string, DataFieldsTableColumnIdBrand> => str.startsWith('col:'),
  'ColumnId',
);
export type DataFieldsTableColumnId = t.TypeOf<typeof RDataFieldsTableColumnId>;

export function brandDataFieldsTableColumnId(id: string): DataFieldsTableColumnId {
  return `col:${id}` as DataFieldsTableColumnId;
}

export function makeDataFieldsColumnId(columns: ReadonlyArray<DataFieldsTableColumnModel>) {
  // Best effort to find a small int ID to get a readable, unique column id.
  // To avoid infinite loops, we have a UUID fallback, but realistically, it should never be hit.
  for (let i = columns.length; i < 1000; ++i) {
    const candidateId = brandDataFieldsTableColumnId(`${i}`);
    if (!columns.some((column) => column.id === candidateId)) {
      return candidateId;
    }
  }
  return brandDataFieldsTableColumnId(uuid());
}

const RDataFieldsTableData = t.record(RDataFieldsTableColumnId, t.union([t.string, t.undefined]));
export interface DataFieldsTableData extends t.TypeOf<typeof RDataFieldsTableData> {}

const RDataFieldsTableColumn = t.type({
  id: RDataFieldsTableColumnId,
  name: t.string,
  type: makeEnumRuntimeType(DataFieldsTableColumnTypesEnum),
});
export interface DataFieldsTableColumnModel extends t.TypeOf<typeof RDataFieldsTableColumn> {}

export const RDataFieldsTable = t.type({
  rows: t.array(RDataFieldsTableData),
  columns: t.array(RDataFieldsTableColumn),
});
export interface DataFieldsTableModel extends t.TypeOf<typeof RDataFieldsTable> {}

function parseDataFieldsTable(src: any): DataFieldsTableModel {
  return {
    rows: src.rows,
    columns: src.columns,
  };
}

export const ApiDataFieldsTable = makeApiType('DataFieldsTable', RDataFieldsTable, parseDataFieldsTable);
